import React, { useState, useEffect, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { sanitizeHtml } from '../utils/sanitizeHtml';

/**
 * WYSIWYG editor component using React-Quill
 * 
 * @param {Object} props
 * @param {string} props.value The HTML content to edit
 * @param {Function} props.onChange Callback when content changes
 * @param {string} props.placeholder Placeholder text when content is empty
 * @param {number} props.minHeight Minimum height in pixels
 * @param {string} props.name Form field name
 * @param {string} props.className Additional CSS classes
 */
const SimpleEditor = ({ 
  value = '', 
  onChange, 
  placeholder = 'Enter text here...', 
  minHeight = 150,
  name,
  className = '',
  ...props
}) => {
  // State to track the editor's content
  const [editorContent, setEditorContent] = useState(value);
  const quillRef = useRef(null);
  
  // Update editor content when the value prop changes
  useEffect(() => {
    if (value !== editorContent) {
      setEditorContent(value);
    }
  }, [value, editorContent]);

  // Handle content changes
  const handleChange = (content) => {
    // Apply HTML sanitization for security
    const sanitized = sanitizeHtml(content);
    setEditorContent(sanitized);
    
    // Call onChange with properly structured event data
    if (onChange) {
      onChange({
        target: {
          name,
          value: sanitized
        }
      });
    }
  };

  // Configure Quill toolbar options
  const modules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'list': 'bullet' }],
      ['clean']
      // Removed undo/redo as they're not supported in the default toolbar
    ],
    clipboard: {
      // Restrict pasted content to only text (no formatting)
      matchVisual: false
    },
    keyboard: {
      bindings: {
        tab: false // Allow tab to move focus out of editor
      }
    }
  };
  
  // Allowed formats (restricts to only these formats for security)
  const formats = [
    'bold', 'italic', 'underline',
    'list', 'bullet'
  ];

  // Custom CSS for editor container - using camelCase for React styles
  const editorStyle = {
    minHeight: minHeight - 42, // Adjust for toolbar height
  };
  
  // Additional class to set editor min-height via CSS
  const editorClassName = `quill-editor-custom ${className}`;

  return (
    <div className={`border rounded ${editorClassName}`} style={{ minHeight }}>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={editorContent}
        onChange={handleChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        style={editorStyle} 
        {...props}
      />
    </div>
  );
};

export default SimpleEditor; 